import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

//Texture like nodes in older Blender versions used to support colormapping and texture mapping directly in the properties.
//This is no longer supported and the corresponding nodes should be used, they are ignored by Blender and Cycles and are only kept for compatibility.
//If you want post-processing on a texture’s color, you’d instead use nodes like MixRGB, Hue/Saturation, or RGB Curves after the texture node.
//We keep these legacy properties for now to assure serializing compatibility to the old format

/*colorMappingBlendColor: "color" // found in inputs: -; found in settings: color; inferred from data: float3
colorMappingBlendFactor: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingBlendType: "string" // found in inputs: -; found in settings: string; inferred from data: string
colorMappingBrightness: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampColorMode: "string" // found in inputs: -; found in settings: string; inferred from data: string
colorMappingColorRampCyclesRampAlphaTable: "floatArray" // found in inputs: -; found in settings: float_array; inferred from data: floatArray
colorMappingColorRampCyclesRampColorTable: "colorArray" // found in inputs: -; found in settings: vector_array; inferred from data: float3Array
colorMappingColorRampElements_0Alpha: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_0Color_0: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_0Color_1: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_0Color_2: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_0Color_3: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_0Position: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Alpha: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Color_0: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Color_1: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Color_2: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Color_3: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampElements_1Position: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingColorRampHueInterpolation: "string" // found in inputs: -; found in settings: string; inferred from data: string
colorMappingColorRampInterpolation: "string" // found in inputs: -; found in settings: string; inferred from data: string
colorMappingContrast: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingSaturation: "float" // found in inputs: -; found in settings: float; inferred from data: float
colorMappingUseColorRamp: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean*/
export const keepLegacyColorMapping = {
    colorMappingBlendColor: z.optional(color),
    colorMappingBlendFactor: z.optional(z.number()),
    colorMappingBlendType: z.optional(z.string()),
    colorMappingBrightness: z.optional(z.number()),
    colorMappingColorRampColorMode: z.optional(z.string()),
    colorMappingColorRampCyclesRampAlphaTable: z.optional(z.array(z.number())),
    colorMappingColorRampCyclesRampColorTable: z.optional(z.array(color)),
    colorMappingColorRampElements_0Alpha: z.optional(z.number()),
    colorMappingColorRampElements_0Color_0: z.optional(z.number()),
    colorMappingColorRampElements_0Color_1: z.optional(z.number()),
    colorMappingColorRampElements_0Color_2: z.optional(z.number()),
    colorMappingColorRampElements_0Color_3: z.optional(z.number()),
    colorMappingColorRampElements_0Position: z.optional(z.number()),
    colorMappingColorRampElements_1Alpha: z.optional(z.number()),
    colorMappingColorRampElements_1Color_0: z.optional(z.number()),
    colorMappingColorRampElements_1Color_1: z.optional(z.number()),
    colorMappingColorRampElements_1Color_2: z.optional(z.number()),
    colorMappingColorRampElements_1Color_3: z.optional(z.number()),
    colorMappingColorRampElements_1Position: z.optional(z.number()),
    colorMappingColorRampHueInterpolation: z.optional(z.string()),
    colorMappingColorRampInterpolation: z.optional(z.string()),
    colorMappingContrast: z.optional(z.number()),
    colorMappingSaturation: z.optional(z.number()),
    colorMappingUseColorRamp: z.optional(z.boolean()),
}

/*textureMappingMapping: "string" // found in inputs: -; found in settings: string; inferred from data: string
textureMappingMappingX: "string" // found in inputs: -; found in settings: string; inferred from data: string
textureMappingMappingY: "string" // found in inputs: -; found in settings: string; inferred from data: string
textureMappingMappingZ: "string" // found in inputs: -; found in settings: string; inferred from data: string
textureMappingMax: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
textureMappingMin: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
textureMappingRotation: "euler" // found in inputs: -; found in settings: euler; inferred from data: float3
textureMappingScale: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
textureMappingTranslation: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
textureMappingUseMax: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
textureMappingUseMin: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
textureMappingVectorType: "string" // found in inputs: -; found in settings: string; inferred from data: string*/
export const keepLegacyTextureMapping = {
    textureMappingMapping: z.optional(z.string()),
    textureMappingMappingX: z.optional(z.string()),
    textureMappingMappingY: z.optional(z.string()),
    textureMappingMappingZ: z.optional(z.string()),
    textureMappingMax: z.optional(vec3),
    textureMappingMin: z.optional(vec3),
    textureMappingRotation: z.optional(vec3),
    textureMappingScale: z.optional(vec3),
    textureMappingTranslation: z.optional(vec3),
    textureMappingUseMax: z.optional(z.boolean()),
    textureMappingUseMin: z.optional(z.boolean()),
    textureMappingVectorType: z.optional(z.string()),
}
